import makeStyles from '@mui/styles/makeStyles';
import AppBar from "@mui/material/AppBar";
import IconButton from '@mui/material/IconButton';
import Menu from "@mui/material/Menu";
import Toolbar from "@mui/material/Toolbar";
import MenuIcon from '@mui/icons-material/Menu';
import {logoutUser} from "actions/authAction";
import {getUnreadCount} from "actions/chatAction";
import {history} from 'common/history';
import {initials, NAV_BAR_HEIGHT} from 'common/helpers';
import {get} from "lodash";
import React, { useEffect } from "react";
import {connect} from 'react-redux';
import AccountMenu from "./AccountMenu";
import SignedInLinks from "./SignedInLinks";
import logo from 'common/assets/logo-with-name.webp';
import LanguageDropdown from "components/LanguageDropdown";
import { useDispatch, useSelector } from 'react-redux';
import {color, font, radius, shadow, space, type} from "common/tokens";

const useStyles = makeStyles(theme => ({
  appBar: {
    background: color.SURFACE,
    borderBottom: `1px solid ${color.DIVIDER}`,
    padding: '0 32px',
    [theme.breakpoints.down('md')]: {
      padding: '0 8px',
    },
  },
  toolbar: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    minHeight: NAV_BAR_HEIGHT,
    height: NAV_BAR_HEIGHT,
    gap: 32,
    [theme.breakpoints.down('md')]: {
      gap: 8,
    },
  },
  menuButton: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  logo: {
    width: 71,
    display: 'block',
  },
  navHorizontal: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    paddingLeft: 12,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  account: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginLeft: 'auto',
  },
  userName: {
    ...type.meta,
    fontWeight: font.MEDIUM,
    color: color.TEXT,
    margin: 0,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  avatarButton: {
    padding: 0,
  },
  avatar: {
    ...type.label,
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: color.PURPLE,
    color: color.ON_PURPLE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  // The profile photo comes back on the login profile call; initials are the
  // fallback for an account that has not uploaded one.
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
  },
  hamburgerIcon: {
    color: color.ACCENT,
  },
  list: {
    padding: 0,
  },
  popOver: {
    marginTop: 12,
    borderRadius: radius.PANEL,
    boxShadow: shadow.POPOVER,
  },
  ncsBanner: {
    ...type.body,
    background: color.SURFACE_MUTED,
    color: color.TEXT,
    padding: '8px 0',
    textAlign: 'center',
    width: '100%',
  },
}));


const Navbar = ({ auth, displayName, picture, currentPath, logout }) => {
  const unreadCounts = useSelector(state => state.chat.channelUnreadCount);
  const totalUnread = unreadCounts.reduce((sum, item) => sum + item.count, 0);
  const dispatch = useDispatch();
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [hamburgerAnchor, setHamburgerAnchor] = React.useState(false);
  const [differentEmailFromNCS, setDifferentEmailFromNCS] = React.useState(false);
  const [, setUserAlreadyExist] = React.useState(false);
  const [ncsEmail, setNcsEmail] = React.useState(null);

  const open = Boolean(anchorEl);
  const openHamburger = Boolean(hamburgerAnchor);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleHamburgerMenu = (event) => {
    setHamburgerAnchor(event.currentTarget);
  };

  const handleHamburgerClose = () => {
    setHamburgerAnchor(false);
  };

  const handleSignOut = () => {
    handleHamburgerClose();
    logout();
  };

  // The badge is in the bar on every page, so the count cannot wait for the
  // chat tab to be opened — nothing else was asking for it.
  useEffect(() => {
    if (auth.isAuthenticated) {
      dispatch(getUnreadCount());
    }
  }, [auth.isAuthenticated, dispatch]);

  useEffect(() => {
    const userEmail = auth?.user?.email;

    const ncsEmailSaved = localStorage.getItem("ncsEmail");
    setNcsEmail(ncsEmailSaved);

    if (userEmail && ncsEmailSaved) {
      if (userEmail.toLowerCase() === ncsEmailSaved.toLowerCase()) {
        setUserAlreadyExist(true);
        localStorage.removeItem("ncsEmail");
      } else {
        setDifferentEmailFromNCS(userEmail !== ncsEmailSaved);
        localStorage.removeItem("ncsEmail");
      }
    }
  }, [auth]);

  const currentPagePath = history.location.pathname;

  const legalPages = ["/privacypolicy", "/termsofservice", "/codeofconduct"];
  if (legalPages.includes(currentPagePath)) {
    return null;
  }

  const navLink = auth.isAuthenticated && <SignedInLinks handleClose={handleClose} currentPath={currentPath} totalUnread={totalUnread} />;

  const Appbar =
    <div>
      <AppBar position="static" elevation={0} className={classes.appBar}>
        <Toolbar variant="dense" disableGutters className={classes.toolbar}>
          <IconButton
            edge="start"
            className={classes.menuButton}
            color="inherit"
            aria-label="menu"
            onClick={handleMenu}
            size="large">
            <MenuIcon className={classes.hamburgerIcon}/>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={open}
            onClose={handleClose}
            PopoverClasses={{paper: classes.popOver}}
            classes={{list: classes.list}}
          >
            {navLink}
          </Menu>

          <img src={logo} className={classes.logo} alt={''}/>

          <div className={classes.navHorizontal}>
            {navLink}
          </div>

          <div className={classes.account}>
            <LanguageDropdown />
            <p className={classes.userName}>{displayName}</p>

            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleHamburgerMenu}
              edge="start"
              className={classes.avatarButton}
              size="large">
              <span className={classes.avatar}>
                {picture
                  ? <img src={picture} className={classes.avatarImage} alt={''}/>
                  : initials(displayName)}
              </span>
            </IconButton>
            <Menu
              id="hamburger-appbar"
              anchorEl={hamburgerAnchor}
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={openHamburger}
              onClose={handleHamburgerClose}
              PopoverClasses={{paper: classes.popOver}}
              classes={
                {
                  list: classes.list,
                }
              }
            >
              <AccountMenu
                displayName={displayName}
                picture={picture}
                role={get(auth, 'user.role')}
                onNavigate={handleHamburgerClose}
                onSignOut={handleSignOut}
              />
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      {(ncsEmail && differentEmailFromNCS) && (
        <div className={classes.ncsBanner}>
          Hello, it looks like you are already logged-in at the Mentor To Go app
          with <em>{auth?.user?.email}</em>. Please continue using your existing
          account.
        </div>
      )}
    </div>;
  return (
    auth.isAuthenticated && Appbar
  );
};


const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    displayName: state.profile.displayName,
    picture: get(state, 'auth.user.picture'),
    currentPath: state.navmenu.currentPath,
    workReadinessScore: state.supplementary.workReadinessScore,
  }
};

const mapDispatchToProps = (dispatch) => {
  return ({
    logout: () => dispatch(logoutUser())
  })
};

export default connect(mapStateToProps, mapDispatchToProps)(Navbar);
